import { useSnackbar } from 'notistack';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Link, useParams } from 'react-router-dom';

import configUrls from '../../config/configUrls';
import axiosInstance from '../../app/axiosInstance';
import { AxiosError } from 'axios';
import { ErrorMessage } from '../../types/ErrorMessage';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import '../../styles/dataTable.css';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import ConfirmDialogList from '../../components/ConfirmDialogList';
import { useDeleteTipoProcessoMutation } from './tipoProcessoApiSlice';
import { IStipoProcesso, TipoProcesso } from '../../types/tipoProcesso/TipoProcesso';

export const TipoProcessoList = () => {
  const [search, setSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  //const [deleteTipoProcesso, deleteTipoProcessoStatus] = useDeleteTipoProcessoMutation();
  const [deleteTipoProcesso, { error, isLoading, isSuccess }] = useDeleteTipoProcessoMutation();

  const [tipoprocesso, setTipoProcesso] = useState<TipoProcesso[]>([]);

  const [tipoprocessoToDelete, setTipoProcessoToDelete] = useState<TipoProcesso>(IStipoProcesso);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchDados();
    }, 700);
  }, [search]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tipo-processo/filter?search=${search}`);
      setTipoProcesso(response.data);
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      enqueueSnackbar('Erro ao efetuar a consulta. Tente novamente mais tarde', { variant: 'error' });

      let erro: ErrorMessage | undefined;
      if (axiosError.response) {
        erro = axiosError.response.data as ErrorMessage;
      }
      if (erro && erro.message) {
        enqueueSnackbar(erro.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Mensagem de erro não disponível', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Apenas foca no input após a execução da pesquisa
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [tipoprocesso]);

  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };

  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteTipoProcesso({ id: tipoprocessoToDelete.id ? tipoprocessoToDelete.id : '' });
  };
  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      fetchDados();
    }
    if (error) {
      enqueueSnackbar('Registro não deletado!', { variant: 'error' });
    }
  }, [isSuccess, error]);

  if (loading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  const header = (
    <>
      <div className="flex flex-wrap justify-content-end gap-2">
        <span>Tipos de Processo(s) encontrado(s): {tipoprocesso.length}</span>
      </div>
    </>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Editar">
          <Link to={`${configUrls.TIPO_PROCESSO_EDIT_LK}${rowData.id}`}>
            <IconButton color="secondary" aria-label="edit">
              <EditIcon />
            </IconButton>
          </Link>
        </Tooltip>

        <Tooltip title="Excluir">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleExcluirtClick = (rowData: any) => {
    console.log(rowData.id);
    setTipoProcessoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse TipoProcesso?', tipoprocessoToDelete.nome ? tipoprocessoToDelete.nome : '']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />

      <Paper>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Lista de Tipos de Processos
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button variant="contained" color="secondary" component={Link} to={configUrls.TIPO_PROCESSO_CREATE_RT}>
                    Novo
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChange} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>

          <Grid item xs={12} gap={2} mt={3}>
            <FormControl fullWidth>
              <DataTable
                value={tipoprocesso}
                paginator
                rows={15}
                showGridlines={true}
                size="small"
                columnResizeMode="expand"
                resizableColumns
                dataKey="id"
                header={header}
                className="custom-datatable"
              >
                <Column field="id" header="ID" hidden sortable style={{ width: '25%' }}></Column>
                <Column field="nome" header="Nome" sortable style={{ maxWidth: '250px', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}></Column>
                <Column field="descricao" header="Descrição" sortable style={{ maxWidth: '250px', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}></Column>
                <Column field="acoes" header="Ações" body={renderAction} style={{ width: 'auto' }}></Column>
              </DataTable>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};
